// animatedLoops/AnimatedLoopTile.jsx
//
// One grid tile. FROZEN-POSTER-ON-TILES: a tile shows its static first-frame
// poster by default. PLAY-ONE-AT-A-TIME (NEM-8, deck concept 07-gif-play-rules):
// the PARENT decides which single tile animates (the one under pointer/focus, or
// the selected one when nothing is active) and passes it as `animate` — so at most
// ONE tile ever moves. Under prefers-reduced-motion nothing animates
// (AnimatedLoopImage enforces that).
//
// A11y (NEM-5): the parent runs a roving-tabindex listbox, so each tile takes a
// `tabIndex` and forwards its ref for programmatic focus, and shows a focus ring
// (box-shadow) distinct from the selected border. Selection accent is the brand
// accent #9B5AF8 (NEM-7), matching the sibling "Reuse Library" control.

import React, { useState, forwardRef } from "react";
import PropTypes from "prop-types";
import AnimatedLoopImage from "./AnimatedLoopImage";

const SELECT_ACCENT = "#9B5AF8"; // NEM-7 — one selection accent, brand-faithful

const AnimatedLoopTile = forwardRef(
  (
    {
      loop,
      palette,
      selected,
      animate = false,
      tabIndex = -1,
      onSelect,
      onActivate,
      onDeactivate,
    },
    ref
  ) => {
    const [focused, setFocused] = useState(false);

    return (
      <button
        ref={ref}
        id={`al-opt-${loop.id}`}
        type="button"
        role="option"
        aria-selected={selected}
        aria-label={`${loop.name}, ${loop.motion}`}
        title={`${loop.name} — ${loop.motion}`}
        tabIndex={tabIndex}
        onClick={() => onSelect(loop)}
        onMouseEnter={() => onActivate && onActivate(loop.id)}
        onMouseLeave={() => onDeactivate && onDeactivate(loop.id)}
        onFocus={() => {
          setFocused(true);
          onActivate && onActivate(loop.id);
        }}
        onBlur={() => {
          setFocused(false);
          onDeactivate && onDeactivate(loop.id);
        }}
        style={{
          padding: 0,
          cursor: "pointer",
          background: "#fff",
          border: selected
            ? `2px solid ${SELECT_ACCENT}`
            : "1px solid #e1e1e1",
          borderRadius: 10,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          textAlign: "left",
          outline: "none",
          // Focus ring distinct from the selected border (NEM-5).
          boxShadow: focused ? `0 0 0 3px rgba(155, 90, 248, 0.35)` : "none",
        }}
      >
        <span style={{ display: "block", height: 52, width: "100%" }}>
          <AnimatedLoopImage
            loop={loop}
            palette={palette}
            animate={animate}
            style={{ height: 52 }}
          />
        </span>
        <span
          style={{
            padding: "5px 8px 6px",
            fontFamily: "DM Sans, sans-serif",
            lineHeight: 1.2,
          }}
        >
          <span style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#123051" }}>
            {loop.name}
          </span>
          <span style={{ display: "block", fontSize: 11, color: "#565556" }}>
            {loop.motion}
          </span>
        </span>
      </button>
    );
  }
);

AnimatedLoopTile.displayName = "AnimatedLoopTile";

AnimatedLoopTile.propTypes = {
  loop: PropTypes.object.isRequired,
  palette: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  selected: PropTypes.bool,
  animate: PropTypes.bool,
  tabIndex: PropTypes.number,
  onSelect: PropTypes.func.isRequired,
  onActivate: PropTypes.func,
  onDeactivate: PropTypes.func,
};

export default AnimatedLoopTile;
